import { useEffect, useRef, useState } from "react";
import { useContentState } from "../../content/ContentContext";

const CURRENT_YEAR = new Date().getFullYear();

/** 不蒜子访问统计（免费、免注册）。接近页脚时才用 HTTPS JSONP 加载。 */
function BusuanziStats() {
  const [failed, setFailed] = useState(false);
  const [stats, setStats] = useState<{ pageViews: number; visitors: number } | null>(null);
  const statsRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const target = statsRef.current;
    if (!target || stats) return;

    let observer: IntersectionObserver | null = null;
    let visibilityTimer = 0;
    let script: HTMLScriptElement | null = null;
    let callbackName = "";
    const globalRecord = window as unknown as Record<string, unknown>;

    const cleanupJsonp = () => {
      script?.remove();
      script = null;
      if (callbackName) delete globalRecord[callbackName];
    };

    const loadStats = () => {
      if (script || stats) return;
      const nonce = window.crypto.getRandomValues(new Uint32Array(1))[0];
      callbackName = `BusuanziCallback_${nonce}`;
      globalRecord[callbackName] = (payload: { site_pv?: number; site_uv?: number }) => {
        if (Number.isFinite(payload.site_pv) && Number.isFinite(payload.site_uv)) {
          setStats({ pageViews: payload.site_pv ?? 0, visitors: payload.site_uv ?? 0 });
        } else {
          setFailed(true);
        }
        cleanupJsonp();
      };

      script = document.createElement("script");
      script.src = `https://busuanzi.ibruce.info/busuanzi?jsonpCallback=${encodeURIComponent(callbackName)}`;
      script.async = true;
      script.referrerPolicy = "strict-origin-when-cross-origin";
      script.onerror = () => {
        setFailed(true);
        cleanupJsonp();
      };
      document.body.appendChild(script);
    };

    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          window.clearTimeout(visibilityTimer);
          if (!entries.some((entry) => entry.isIntersecting)) return;
          // 首页代码块加载时 Footer 会短暂位于 Suspense 占位页下方；延迟复核可避免误加载统计。
          visibilityTimer = window.setTimeout(() => {
            const rect = target.getBoundingClientRect();
            if (rect.top <= window.innerHeight + 300 && rect.bottom >= -300) {
              loadStats();
              observer?.disconnect();
            }
          }, 800);
        },
        { rootMargin: "300px" }
      );
      observer.observe(target);
    } else {
      loadStats();
    }

    return () => {
      window.clearTimeout(visibilityTimer);
      observer?.disconnect();
      cleanupJsonp();
    };
  }, [stats]);

  if (failed) return null;
  return (
    <p ref={statsRef} className="text-[12px] t-50" aria-live="polite">
      本站总访问量 <span>{stats?.pageViews ?? "-"}</span> 次 · 访客 <span>{stats?.visitors ?? "-"}</span> 人
    </p>
  );
}

export default function Footer() {
  // 内容未就绪时 Footer 也会渲染（加载页），此时不显示统计
  const { content } = useContentState();
  const provider = content?.site.analytics.provider ?? "none";
  return (
    <footer className="relative mx-auto max-w-6xl px-6 pb-12 pt-8">
      <div className="glass glass-sheen rounded-[28px] px-8 py-10">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#6ec1ff] via-[#2997ff] to-[#7d7aff] text-[14px] font-bold text-white">
                L
              </span>
              <span className="text-[17px] font-semibold tracking-tight t-90">LetsLearning</span>
            </div>
            <p className="mt-3 max-w-sm text-[13px] leading-relaxed t-40">
              编程知识 · 原理科普 · 实用工具。视频内容托管于哔哩哔哩，本站仅提供聚合与站内播放。
            </p>
          </div>
          <div className="flex flex-col items-start gap-3 md:items-end">
            <a
              href="https://space.bilibili.com/25488437"
              target="_blank"
              rel="noreferrer"
              className="glass-chip flex h-11 items-center gap-2 rounded-full px-5 text-[13px] font-medium t-80 transition-all duration-500 hover-surface-2 active:scale-[0.97]"
              style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                <path d="M17.813 4.653h.854c1.51.054 2.769.578 3.773 1.574 1.004.995 1.524 2.249 1.56 3.76v7.36c-.036 1.51-.556 2.769-1.56 3.773s-2.262 1.524-3.773 1.56H5.333c-1.51-.036-2.769-.556-3.773-1.56S.036 18.858 0 17.347v-7.36c.036-1.511.556-2.765 1.56-3.76 1.004-.996 2.262-1.52 3.773-1.574h.774l-1.174-1.12a1.234 1.234 0 0 1-.373-.906c0-.356.124-.658.373-.907l.027-.027c.267-.249.573-.373.92-.373.347 0 .653.124.92.373L9.653 4.44c.071.071.134.142.187.213h4.267a.836.836 0 0 1 .16-.213l2.853-2.747c.267-.249.573-.373.92-.373.347 0 .662.151.929.4.267.249.391.551.391.907 0 .355-.124.657-.373.906zM5.333 7.24c-.746.018-1.373.276-1.88.773-.506.498-.769 1.13-.786 1.894v7.52c.017.764.28 1.395.786 1.893.507.498 1.134.756 1.88.773h13.334c.746-.017 1.373-.275 1.88-.773.506-.498.769-1.129.786-1.893v-7.52c-.017-.765-.28-1.396-.786-1.894-.507-.497-1.134-.755-1.88-.773zM8 11.107c.373 0 .684.124.933.373.25.249.383.56.4.933v2.587c0 .373-.124.684-.373.933-.249.25-.56.374-.933.374s-.684-.125-.933-.374-.373-.56-.373-.933v-2.587c0-.373.124-.684.373-.933.249-.249.56-.373.933-.373zm8 0c.373 0 .684.124.933.373.25.249.383.56.4.933v2.587c-.017.373-.141.684-.373.933-.232.25-.551.374-.933.374-.383 0-.692-.125-.933-.374-.242-.249-.364-.56-.373-.933v-2.587c0-.373.124-.684.373-.933.241-.249.55-.373.933-.373z" />
              </svg>
              在 B 站关注 LetsLearning
            </a>
            <p className="text-[12px] t-50">© {CURRENT_YEAR} LetsLearning</p>
            {provider === "busuanzi" && <BusuanziStats />}
          </div>
        </div>
      </div>
    </footer>
  );
}
